import React, { useEffect, useMemo, useState } from 'react';
import { CheckCircle2 } from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';
import Header from '../Components/cleaning/Header.jsx';

const API_URL = process.env.REACT_APP_API_URL;

function formatSquareFeet(value) {
  if (value === null || value === undefined || value === '') {
    return '—';
  }

  const numericValue = Number(value);
  if (Number.isFinite(numericValue)) {
    return `${numericValue.toLocaleString()} sq ft`;
  }

  return value;
}

function formatRooms(value) {
  if (!value) return '—';
  if (value.toLowerCase?.() === 'studio') return 'Studio';

  const numericValue = Number(value);
  if (Number.isFinite(numericValue) && numericValue > 0) {
    return numericValue === 1 ? '1 Room' : `${numericValue} Rooms`;
  }

  return value;
}

function formatBaths(value) {
  if (!value) return '—';
  const numericValue = Number(value);
  if (Number.isFinite(numericValue)) {
    return numericValue === 1 ? '1 Bathroom' : `${numericValue} Bathrooms`;
  }

  return value;
}

function formatTotal(total, currency = 'usd') {
  if (total === null || total === undefined) {
    return '—';
  }

  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase()
    }).format(total);
  } catch (_error) {
    return `$${Number(total).toFixed(2)}`;
  }
}

export default function Success() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');

  const [state, setState] = useState({
    loading: true,
    error: '',
    data: null
  });

  useEffect(() => {
    if (!sessionId) {
      setState({
        loading: false,
        error: 'We couldn’t find your booking details. Please use the link in your confirmation email.',
        data: null
      });
      return;
    }

    let isMounted = true;

    (async () => {
      try {
        const baseUrl = API_URL || '';
        const response = await fetch(`${baseUrl}/api/checkout-session?session_id=${encodeURIComponent(sessionId)}`);

        if (!response.ok) {
          throw new Error('Unable to load your booking details right now.');
        }

        const payload = await response.json();

        if (isMounted) {
          setState({ loading: false, error: '', data: payload });
        }
      } catch (error) {
        if (isMounted) {
          setState({
            loading: false,
            error: error.message || 'Something went wrong while loading your booking.',
            data: null
          });
        }
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [sessionId]);

  const { loading, error, data } = state;

  const summaryRows = useMemo(() => {
    if (!data) return [];

    return [
      { label: 'Email:', value: data.customerEmail || '—' },
      { label: 'Service Type:', value: data.serviceType || '—' },
      { label: 'Square Feet:', value: formatSquareFeet(data.squareFeet) },
      { label: 'Rooms:', value: formatRooms(data.rooms) },
      { label: 'Bathrooms:', value: formatBaths(data.bathrooms) },
      { label: 'Extra Services:', value: data.extras || 'None' }
    ];
  }, [data]);

  const totalDisplay = useMemo(() => {
    if (!data) return '—';
    return formatTotal(data.total, data.currency);
  }, [data]);

  const customerEmail = data?.customerEmail || '';
  const mailtoLink = customerEmail ? `mailto:${customerEmail}` : undefined;
    const handleCheckMailClick = (event) => {
    event.preventDefault();

    if (typeof window === 'undefined') return;

    const userEmail = customerEmail.toLowerCase();

    // Detect provider based on domain
    let mailProvider = '';
    if (userEmail.includes('@gmail.')) {
      mailProvider = 'gmail';
    } else if (userEmail.includes('@yahoo.')) {
      mailProvider = 'yahoo';
    } else if (userEmail.includes('@outlook.') || userEmail.includes('@hotmail.') || userEmail.includes('@live.')) {
      mailProvider = 'outlook';
    } else if (userEmail.includes('@aol.')) {
      mailProvider = 'aol';
    }

    // Open the appropriate inbox URL
    switch (mailProvider) {
      case 'gmail':
        window.open('https://mail.google.com/mail/u/0/', '_blank', 'noopener,noreferrer');
        break;
      case 'yahoo':
        window.open('https://mail.yahoo.com/', '_blank', 'noopener,noreferrer');
        break;
      case 'outlook':
        window.open('https://outlook.live.com/mail/', '_blank', 'noopener,noreferrer');
        break;
      case 'aol':
        window.open('https://mail.aol.com/', '_blank', 'noopener,noreferrer');
        break;
      default:
        // Fallback — open default mail app
        window.location.href = mailtoLink || 'mailto:';
        break;
    }
  };


  return (
    <div className="success-page">
      <Header showTotal={false} />
      <main className="page-shell success-page__shell">
        <div className="success-card card">
          <div className="success-icon">
            <CheckCircle2 aria-hidden="true" />
          </div>

          <div className="success-copy">
            <h1 className="success-heading">Payment successful — you&apos;re all set</h1>
            <p className="success-subheading">
              Thanks for booking with Fresh Shine. Your cleaning is confirmed.
              {customerEmail ? (
                <>
                  {' '}A receipt and booking details were sent to{' '}
                  <span className="success-email">{customerEmail}</span>.
                </>
              ) : (
                ' A receipt and booking details will arrive shortly.'
              )}
            </p>
          </div>

          {loading && (
            <div className="success-status" role="status">
              Fetching your booking details…
            </div>
          )}

          {error && !loading && (
            <div className="success-error" role="alert">
              <p>{error}</p>
              <Link to="/" className="success-link">Return to booking</Link>
            </div>
          )}

          {!loading && !error && data && (
            <>
              <div className="success-summary card panel-cream">
                <h2 className="success-summary__heading">Order Summary</h2>
                <div className="payment-divider" />
                <dl className="success-summary__list">
                  {summaryRows.map((row) => (
                    <div key={row.label} className="success-summary__row">
                      <dt>{row.label}</dt>
                      <dd>{row.value}</dd>
                    </div>
                  ))}
                </dl>
                <div className="payment-divider" />
                <div className="success-total">
                  <span className="success-total__label">Total:</span>
                  <span className="success-total__value">{totalDisplay}</span>
                </div>
              </div>

              <div className="success-actions">
                <a
                  href={mailtoLink || 'mailto:'}
                  className="success-mail-button"
                  onClick={handleCheckMailClick}
                >
                  Check Mail
                </a>
                <Link to="/" className="success-link">Back to booking</Link>
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
